import React from "react";
import useLang from "Shared/hooks/useLanguage";
import PathologyReportSelect from "../PathologyReportSelect";
import {
  DEFAULT_IHC_COMMENT,
  IHC_PANELS,
  ihcMarkerOptions,
  type IhcPanelKey,
  type SelectOption,
} from "../pathologyReportData";

type Props = {
  activePanels: IhcPanelKey[];
  markerValues: Record<string, SelectOption | null>;
  ihcComment: string;
  onTogglePanel: (key: IhcPanelKey) => void;
  onMarkerChange: (fieldId: string, value: SelectOption | null) => void;
  onCommentChange: (value: string) => void;
};

const IhcAncillaryTab = ({
  activePanels,
  markerValues,
  ihcComment,
  onTogglePanel,
  onMarkerChange,
  onCommentChange,
}: Props) => {
  const { t } = useLang();
  const hasActivePanel = activePanels.length > 0;

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {IHC_PANELS.map((panel) => {
          const isActive = activePanels.includes(panel.key);
          return (
            <button
              key={panel.key}
              type="button"
              className={`btn btn-sm fw-semibold px-4 ${
                isActive ? panel.activeClass : "btn-light border"
              }`}
              onClick={() => onTogglePanel(panel.key)}
            >
              {isActive ? (
                <>
                  <i className="bi bi-check-lg me-1" />
                  {t(panel.label)}
                </>
              ) : (
                t(panel.addLabel)
              )}
            </button>
          );
        })}
      </div>

      {!hasActivePanel && (
        <div
          className="rounded-2 px-3 py-2 mb-4 fs-7"
          style={{ backgroundColor: "#fff8e1", border: "1px solid #ffe082" }}
        >
          {t("Click a panel above to add IHC markers")}
        </div>
      )}

      {IHC_PANELS.filter((p) => activePanels.includes(p.key)).map((panel) => (
        <div key={panel.key} className="border rounded-3 overflow-hidden mb-4">
          <div className="bg-light px-4 py-2 fw-semibold text-gray-700">
            {t(panel.label)}
          </div>
          <div className="p-4">
            <div className="row g-3">
              {panel.fields.map((field) => (
                <div
                  key={field.id}
                  className={
                    panel.fields.length === 5 && field.id === "gcdfp"
                      ? "col-md-3"
                      : panel.fields.length >= 4
                        ? "col-md-3"
                        : "col-md-4"
                  }
                >
                  <label className="form-label fw-semibold text-gray-700 fs-7">
                    {t(field.label)}
                  </label>
                  <PathologyReportSelect
                    value={markerValues[field.id] ?? null}
                    onChange={(v) => onMarkerChange(field.id, v)}
                    options={ihcMarkerOptions}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      <div>
        <label className="form-label fw-semibold text-gray-700">
          {t("IHC interpretation/comment")}
        </label>
        <textarea
          className="form-control rounded-2"
          rows={5}
          value={ihcComment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder={t(DEFAULT_IHC_COMMENT)}
        />
      </div>
    </div>
  );
};

export default IhcAncillaryTab;
